import { ArmyList, ArmyListUnit } from '@Models';
import {
  ArmyListActionState,
  ArmyListState,
  buildArmyListState,
} from '@Stores/index';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const sumArmyListPoints = (armyListUnits: Array<ArmyListUnit>): number =>
  armyListUnits
    .filter((item) => !item.isDeleted && item.is_enabled)
    .map((item) => item.points)
    .reduce((a: number, b: number) => a + b, 0);

export interface ArmyListUnitTogglePayload {
  id: string;
  isEnabled: boolean;
}

const initialState: ArmyListState = buildArmyListState({
  entity: null,
}) satisfies ArmyListState as ArmyListState;

const armyListSlice = createSlice({
  name: 'army-list',
  initialState,
  reducers: {
    setError(state: ArmyListState, action: PayloadAction<string>) {
      state.errorMessage = action.payload;
      state.hasError = true;
    },
    setLoading(state: ArmyListState, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setArmyList(state: ArmyListState, action: PayloadAction<ArmyList>) {
      state.entity = {
        ...action.payload,
        points: sumArmyListPoints(action.payload.army_list_units),
      };
      state.id = action.payload.id;
      state.isLoading = false;
      state.hasError = false;
      state.errorMessage = null;
    },
    setArmyOrganisation(state: ArmyListState, action: PayloadAction<number>) {
      state.entity = {
        ...state.entity!,
        army_organisation_id: action.payload,
      };
    },
    updateArmyListUnit(
      state: ArmyListState,
      action: PayloadAction<ArmyListUnit>,
    ) {
      const armyListUnits = state.entity!.army_list_units.map((item) =>
        item.id === action.payload.id ? action.payload : item,
      );

      state.entity = {
        ...state.entity!,
        army_list_units: armyListUnits,
        points: sumArmyListPoints(armyListUnits),
      };
    },
    updateArmyListUnitErrorSync(
      state: ArmyListState,
      action: PayloadAction<string>,
    ) {
      const armyListUnits = state.entity!.army_list_units.map((item) => {
        if (item.id === action.payload)
          return {
            ...item,
            errorSync: true,
          };

        return item;
      });

      state.entity = {
        ...state.entity!,
        army_list_units: armyListUnits,
        points: sumArmyListPoints(armyListUnits),
      };
    },
    updateArmyListUnitEnabled(
      state: ArmyListState,
      action: PayloadAction<ArmyListUnitTogglePayload>,
    ) {
      // PayloadAction<{ id: string; isEnabled: boolean }>
      const armyListUnits = state.entity!.army_list_units.map((item) => {
        if (item.id === action.payload.id)
          return {
            ...item,
            is_enabled: action.payload.isEnabled,
          };

        return item;
      });

      state.entity = {
        ...state.entity!,
        army_list_units: armyListUnits,
        points: sumArmyListPoints(armyListUnits),
      };
    },
    armyListUnitDelete(
      state: ArmyListState,
      action: PayloadAction<ArmyListUnit>,
    ) {
      const armyListUnits = state.entity!.army_list_units.map((item) => {
        if (item.id === action.payload.id)
          return {
            ...item,
            isDeleted: true,
          };

        if (item.position > action.payload.position)
          return {
            ...item,
            position: item.position - 1,
          };

        return item;
      });

      state.entity = {
        ...state.entity!,
        army_list_units: armyListUnits,
        points: sumArmyListPoints(armyListUnits),
      };
    },
    armyListUnitDeleteSuccess(
      state: ArmyListState,
      action: PayloadAction<string>,
    ) {
      const armyListUnits = state.entity!.army_list_units.filter(
        (item) => item.id != action.payload,
      );

      state.entity = {
        ...state.entity!,
        army_list_units: armyListUnits,
        points: sumArmyListPoints(armyListUnits),
      };
    },
    armyListUnitCreationSuccess(
      state: ArmyListState,
      action: PayloadAction<ArmyListUnit>,
    ) {
      const armyListUnits = state
        .entity!.army_list_units.map((item) => {
          if (item.position >= action.payload.position)
            return {
              ...item,
              position: item.position + 1,
            };

          return item;
        })
        .concat([action.payload])
        .sort((a, b) => a.position - b.position);

      state.entity = {
        ...state.entity!,
        army_list_units: armyListUnits,
        points: sumArmyListPoints(armyListUnits),
      };
    },
    cancel(state: ArmyListState) {
      state.action = null;
    },
    setRequest(
      state: ArmyListState,
      action: PayloadAction<ArmyListActionState>,
    ) {
      state.action = action.payload;
    },
  },
});

export default armyListSlice;
